// Shared HTTP helpers for the Vite/Vue frontend.
// The Rails app keeps using cookie-based sessions and CSRF protection
// (`protect_from_forgery with: :exception`), so any same-origin non-GET request
// must send the token rendered by `csrf_meta_tags` in the layout. This mirrors
// the `$.ajaxPrefilter` installed by `app/assets/javascripts/csrf.js` for the
// legacy jQuery code; that file does not expose a callable helper, so both
// paths read the same `meta[name="csrf-token"]`.
//
// Cross-origin requests are left alone: jQuery's `csrf.js` returns early for
// `options.crossDomain`, and jQuery itself only adds `X-Requested-With` for
// same-origin requests. Adding either header to a cross-origin request would
// force a CORS preflight and, in the Gyazo upload case, break the upload (and
// leak the app's CSRF token to a third party).

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

// True when `path` points at a different origin than the current document.
// Relative URLs are resolved against the document and count as same-origin.
// The fallback base keeps the helper evaluable outside a browser (e.g. a Node
// test harness) while still classifying absolute URLs correctly.
export function isCrossOrigin(path) {
  const base = typeof location !== 'undefined' ? location.href : 'http://localhost/'
  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost'
  try {
    return new URL(path, base).origin !== origin
  } catch {
    return false
  }
}

// `fetch` rejects any GET/HEAD request that carries a body, so both `request`
// and `ajax` must agree on which methods are bodyless.
function methodHasNoBody(method) {
  const normalizedMethod = method.toUpperCase()
  return normalizedMethod === 'GET' || normalizedMethod === 'HEAD'
}

// Turns a plain object into an `application/x-www-form-urlencoded` body.
// Arrays use jQuery's default `$.param` shape (`key[]=v1&key[]=v2`), which
// Rails parses back into an Array (`albums#updateOrder` expects
// `params[:photo_ids]` to be an array). Only a top-level array is expanded
// this way; nested objects/arrays are not supported (no current caller needs
// them) and would be stringified with `String(value)`. Both `null` and
// `undefined` values become an empty value (`key=`), matching jQuery's
// `encodeURIComponent(value == null ? "" : value)`. This matters for
// `primary_photo_id: null`: sending `primary_photo_id=` clears the column,
// whereas omitting the key would leave the previous (now deleted) photo id
// behind. The whole `data` argument may still be `null`/`undefined`, meaning
// "no body". `FormData` (file uploads, multipart boundary left to the
// browser) and `URLSearchParams` are passed through untouched.
export function serializeData(data) {
  if (data === undefined || data === null || data instanceof FormData || data instanceof URLSearchParams) {
    return data
  }

  const params = new URLSearchParams()
  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        params.append(`${key}[]`, item == null ? '' : item)
      })
    } else {
      params.append(key, value == null ? '' : value)
    }
  })
  return params
}

async function request(path, { method = 'GET', body, headers = {}, signal } = {}) {
  const hasNoBodyMethod = methodHasNoBody(method)
  // Keep cross-origin requests as CORS "simple requests": never add the
  // non-safelisted `X-Requested-With` / `X-CSRF-Token` headers (and send no
  // credentials), exactly like jQuery's same-origin-only defaults.
  const crossOrigin = isCrossOrigin(path)
  const response = await fetch(path, {
    method,
    credentials: crossOrigin ? 'omit' : 'same-origin',
    signal,
    headers: {
      Accept: 'application/json',
      ...(crossOrigin ? {} : { 'X-Requested-With': 'XMLHttpRequest' }),
      ...(hasNoBodyMethod || crossOrigin ? {} : { 'X-CSRF-Token': csrfToken() }),
      ...headers,
    },
    body,
  })

  if (!response.ok) {
    throw new Error(`Request ${method} ${path} failed with status ${response.status}`)
  }

  return response
}

// Reads and decodes a successful response body according to `parse`:
//   * `'none'` -> always `null`, body left unread
//   * `'json'` / `'text'` -> decode as requested; an empty body is `null`
//   * `'auto'` -> follow the response Content-Type, empty body is `null`
// `204 No Content` and `205 Reset Content` never carry a body and always yield
// `null`, so `head :ok` endpoints resolve instead of failing on `.json()`.
async function readBody(response, parse) {
  if (parse === 'none') return null
  if (response.status === 204 || response.status === 205) return null

  const text = await response.text()
  if (text === '') return null

  if (parse === 'json') return JSON.parse(text)
  if (parse === 'text') return text

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) return JSON.parse(text)
  return text
}

// Generic replacement for the legacy `$.ajax` calls. `data` may be a plain
// object (form-encoded, arrays as `key[]`), `FormData` (multipart) or
// `URLSearchParams`. For GET/HEAD a form-encoded `data` is appended to the
// query string instead of becoming a request body; the body is then cleared
// (an empty query, e.g. `data: {}`, must not leave an empty body, which
// `fetch` rejects with "Request with GET/HEAD method cannot have body"). Any
// other body (e.g. `FormData`, even empty) is rejected with a `TypeError`
// rather than silently dropped, because those methods cannot carry a body at
// all. `method` is matched case-insensitively, so `'get'` behaves like `'GET'`.
// `parse` defaults to `'auto'`; pass `'json'`, `'text'` or `'none'` to be
// explicit. Non-2xx responses throw an `Error`; an aborted fetch rejects with
// its original `AbortError` unchanged.
export function ajax(path, { method = 'GET', data, parse = 'auto', headers = {}, signal } = {}) {
  let body = serializeData(data)
  let url = path
  const requestHeaders = { ...headers }

  if (methodHasNoBody(method)) {
    if (body instanceof URLSearchParams) {
      const query = body.toString()
      if (query) url = path + (path.includes('?') ? '&' : '?') + query
    } else if (body !== undefined && body !== null) {
      throw new TypeError(`${method.toUpperCase()} request to ${path} cannot have a body`)
    }
    body = undefined
  } else if (body instanceof URLSearchParams) {
    const query = body.toString()
    if (query) {
      requestHeaders['Content-Type'] =
        requestHeaders['Content-Type'] || 'application/x-www-form-urlencoded; charset=UTF-8'
    }
  }

  return request(url, { method, body, headers: requestHeaders, signal }).then((response) =>
    readBody(response, parse)
  )
}

// POSTs `data` as an `application/x-www-form-urlencoded` body (what jQuery's
// `$.ajax` used to send for these endpoints) and parses the JSON response.
// A `FormData` body is passed through untouched so callers can upload files;
// the browser then sets the multipart boundary itself.
export function postForm(path, data, signal) {
  return ajax(path, { method: 'POST', data, parse: 'json', signal })
}
